/**
 * `/auth/return` and `/auth/callback` — the landing routes after centralized login. saas-auth
 * accepts exactly these two return paths; invitation accept comes back on `/auth/callback`,
 * and this console's own sign-in still sends `/auth/return`. The URL carries no code and no
 * token (H §4.1): the refresh cookie is now set, so we run one silent refresh (csrf →
 * refresh) to mint the in-memory bearer, then navigate to the stashed deep link (or home).
 * NEVER `/api/auth/session/exchange` — saas-auth rejects reseller sessions there. A missing
 * session goes to the auth-error page; a session that ended (`revoked` / `misconfigured`)
 * is shown in place. The same element serves both paths; neither redirects to the other.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SkipLink } from '@/components/SkipLink';
import { visuallyHidden } from '@/components/visuallyHidden';
import { bootRefresh, popDeepLink, type BootResult } from '@/auth/silentRefresh';
import { useAuth } from '@/auth/authHooks';
import { HOME_PATH } from '@/routes/home';
import { SessionEnded } from './SessionEnded';

export function AuthReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const started = useRef(false);
  const [result, setResult] = useState<BootResult | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      const outcome = await bootRefresh();
      setResult(outcome);
      if (outcome === 'ok') {
        // The store already holds the token and its expiry — publish, don't re-set.
        setAuthed();
        navigate(popDeepLink() ?? HOME_PATH, { replace: true });
      } else if (outcome === 'signin') {
        navigate('/auth/error', { replace: true });
      }
    })();
  }, [navigate, setAuthed]);

  if (result === 'revoked' || result === 'misconfigured') {
    return <SessionEnded reason={result} />;
  }

  return (
    <>
      <SkipLink />
      <main id="main" tabIndex={-1} style={{ padding: '2rem', textAlign: 'center' }}>
        {/* The page's name follows its state: completing, or the sign-in problem (TMMRSL-0075). */}
        <h1 style={visuallyHidden}>
          {result === 'signin' ? t('auth.errorTitle') : t('auth.completing')}
        </h1>
        <div role="status" aria-live="polite">
          <p>{result === 'signin' ? t('auth.signInFailed') : t('auth.completing')}</p>
        </div>
      </main>
    </>
  );
}
